import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getLocalities } from "../../Redux/Actions/projectActions";
import { updateProjectResale, viewProjectResale } from "../../Redux/Actions/projectResaleActions";
import { viewAmenitiesList } from "../../Redux/Actions/amenitiesActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import {
    viewCategoryList,
    viewSubCategoryList,
} from "../../Redux/Actions/categoryActions";
import {
    viewCityListPagination,
    viewCountryListPagination,
    viewStateListPagination,
} from "../../Redux/Actions/settingsActions";
import { viewBuilderList } from "../../Redux/Actions/builderActions";
import toast from "react-hot-toast";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import AddImage from "../../Global Functions/addImage";
import BrandsNearbySelector from "./brandNearbySelect";
import { ToFirstCapital } from "../../Utils/helpers";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./editResaleProject.css";

const ManualBrandEntry = ({ onAdd }) => {
    const [brandName, setBrandName] = useState("");
    const [brandImage, setBrandImage] = useState("");
    const [uploading, setUploading] = useState(false);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const url = await AddImage(file);
        setUploading(false);
        if (url) setBrandImage(url);
    };

    const handleAdd = () => {
        if (!brandName.trim()) { toast.error("Brand name is required."); return; }
        onAdd({ name: brandName.trim(), image: brandImage, _manual: true });
        setBrandName("");
        setBrandImage("");
    };

    return (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div className="erp-field" style={{ flex: "1 1 180px" }}>
                <label className="erp-label">Brand Name</label>
                <input
                    className="erp-input"
                    type="text"
                    placeholder="e.g. McDonald's"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
                />
            </div>
            <div className="erp-field" style={{ flex: "1 1 180px" }}>
                <label className="erp-label">Brand Logo (optional)</label>
                {brandImage ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={brandImage} alt="brand" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", border: "1px solid #e4e7ec" }} />
                        <button
                            type="button"
                            className="erp-furnish-btn erp-furnish-btn--clear"
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            onClick={() => setBrandImage("")}
                        >Remove</button>
                    </div>
                ) : (
                    <input
                        className="erp-input"
                        type="file"
                        accept="image/*"
                        disabled={uploading}
                        onChange={handleImageUpload}
                    />
                )}
                {uploading && <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>Uploading...</p>}
            </div>
            <div style={{ paddingBottom: 2 }}>
                <button
                    type="button"
                    className="erp-furnish-btn erp-furnish-btn--all"
                    style={{ height: 42, padding: "0 20px" }}
                    onClick={handleAdd}
                >
                    + Add Brand
                </button>
            </div>
        </div>
    );
};

const EditProject = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [categoryChange, setCategoryChange] = useState([]);
    const [projectType, setProjectType] = useState("");
    const [twoDMap, setTwoDMap] = useState([{ title: "", tower_name: "", image: "", isPdf: false }]);
    const [localitiesArray, setLocalitiesArray] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState("");
    const [localitiesPopup, setLocalitiesPopup] = useState(false);
    const [selectedLocalityIds, setSelectedLocalityIds] = useState([]);
    const [localitySearch, setLocalitySearch] = useState("");
    const autocompleteRef = useRef(null);
    const brochureRef = useRef(null);
    // ── Added: investment modal + amenity parent state ──
    const [openInvestmentModal, setOpenInvestmentModal] = useState(false);
    const [selectedAmenityParent, setSelectedAmenityParent] = useState(null);

    const [faqs, setFaqs] = useState([{ question: "", answer: "" }]);
    const [news, setNews] = useState([{ title: "", description: "", image: "", link: "" }]);
    const [ytLinks, setYtLinks] = useState([{ index: 0, url: "" }]);
    const [projectHighlight, setProjectHighlight] = useState([{ title: "", description: "", image: "" }]);
    const [mapAddressInput, setMapAddressInput] = useState("");

    const [coverImgPreview, setCoverImgPreview] = useState("");
    const [newCoverImg, setNewCoverImg] = useState(null);

    const [existingProjectImgs, setExistingProjectImgs] = useState([]);
    const [newProjectImgs, setNewProjectImgs] = useState([]);
    const [subAmenitySearch, setSubAmenitySearch] = useState("");
    const [newProjectImgPreviews, setNewProjectImgPreviews] = useState([]);
    const [builderDropdownOpen, setBuilderDropdownOpen] = useState(false);
    const builderDropdownRef = useRef(null);
    const [landmarkInput, setLandmarkInput] = useState("");
    const [brochureUploading, setBrochureUploading] = useState(false);
    const [terraceInput, setTerraceInput] = useState("");
    const [builderSearch, setBuilderSearch] = useState("");

    const libraries = ["places"];
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries,
    });

    const [projectPayload, setProjectPayload] = useState({
        added_by: "admin",
        project_name: "",
        business_type: "resale",           // ── Added
        construction_status: "Not started",
        is_plot_project: false,
        is_investment_opportunity: false,
        is_assured_return: false,
        is_assured_rental: false,
        is_preleased_property: false,
        is_high_rental_yield: false,
        is_guaranteed_buyback: false,
        is_zero_maintenance: false,
        is_fixed_monthly_income: false,
        is_fractional_ownership: false,
        clubhouse_area: "",
        clubhouse_area_measure_in: "sq.ft",
        rera_id: "",
        builder: "",
        project_type: "",
        categories: [],
        launch_date: "",
        street_address: "",
        lat: "",
        lng: "",
        quantity: "",
        brands_nearby: [],
        city: "",
        currency: "",
        starting_price: "",
        project_area: "",
        area_unit: "acres",
        average_price: "",
        average_rental: "",
        country: "",
        extra_benefits: "",
        hold_time: 48,
        discount_value: "",
        state: "",
        possesion_date: "",
        property_age: "",
        no_of_towers: "",
        no_of_floors_resale: "",
        no_of_plots: "",
        no_of_parks: "",
        green_area_percentage: "",
        community: "",
        key_project_details: [],
        dubai_based_project_type: "",
        about_project: "",
        about_builder: "",
        note: "",
        payment_note: "",
        brochure: "",
        amenities: [],
        cover_photo: "",
        images: [],
        basement: [],
        terrace: [],
        landmarks: [],
        localities: [],
        location: { type: "Point", coordinates: [] }, // ── Added
    });

    const fields = [
        "Assured Return", "Assured Rental", "Pre-leased Property",
        "High Rental Yield", "Guaranteed Buyback Option",
        "Zero Maintainence", "Fixed Monthly Income", "Fractional Ownership",
    ];
    const fieldKeyMap = {
        "Assured Return": "is_assured_return",
        "Assured Rental": "is_assured_rental",
        "Pre-leased Property": "is_preleased_property",
        "High Rental Yield": "is_high_rental_yield",
        "Guaranteed Buyback Option": "is_guaranteed_buyback",
        "Zero Maintainence": "is_zero_maintenance",
        "Fixed Monthly Income": "is_fixed_monthly_income",
        "Fractional Ownership": "is_fractional_ownership",
    };

    const [trueCount, setTrueCount] = useState(0);
    const boolDeps = fields.map((field) => projectPayload[fieldKeyMap[field]]);
    useEffect(() => {
        const count = fields.reduce((sum, field) => sum + (projectPayload[fieldKeyMap[field]] ? 1 : 0), 0);
        setTrueCount(count);
    }, boolDeps);

    const { categories, subCategories } = useSelector((state) => state.categories || {});
    const { countryList } = useSelector((state) => state.country || {});
    const { states } = useSelector((state) => state.states || {});
    const cityState = useSelector((state) => state.city || {});
    const cities = Array.isArray(cityState?.citiesAllList?.data?.data)
        ? cityState.citiesAllList.data.data
        : [];
    const { amenities } = useSelector((state) => state.amenities || {});
    const { enumsData } = useSelector((state) => state.enumsData || {});
    const { localities, localitiesLoading } = useSelector((state) => state.projects || {});
    const { builders } = useSelector((state) => state.builders || {});

    // ── PREFILL DATA ───────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchProject = async () => {
            setLoading(true);
            try {
                const res = await dispatch(viewProjectResale(id));
                const d = res?.data;
                if (!d) { toast.error("Project not found"); navigate("/resale_project"); return; }

                setProjectPayload({
                    added_by: d.added_by || "admin",
                    project_name: d.project_name || "",
                    business_type: d.business_type || "resale",   // ── Added
                    construction_status: d.construction_status || "Not started",
                    is_plot_project: d.is_plot_project || false,
                    is_investment_opportunity: d.is_investment_opportunity || false,
                    is_assured_return: d.is_assured_return || false,
                    is_assured_rental: d.is_assured_rental || false,
                    is_preleased_property: d.is_preleased_property || false,
                    is_high_rental_yield: d.is_high_rental_yield || false,
                    is_guaranteed_buyback: d.is_guaranteed_buyback || false,
                    is_zero_maintenance: d.is_zero_maintenance || false,
                    is_fixed_monthly_income: d.is_fixed_monthly_income || false,
                    is_fractional_ownership: d.is_fractional_ownership || false,
                    clubhouse_area: d.clubhouse_area || "",
                    clubhouse_area_measure_in: d.clubhouse_area_measure_in || "sq.ft",
                    rera_id: d.rera_id || "",
                    builder: d.builder?._id || d.builder?.id || d.builder || "",
                    project_type: d.project_type?._id || d.project_type || "",
                    categories: d.categories?.map((c) => c._id || c) || [],
                    launch_date: d.launch_date ? d.launch_date.split("T")[0] : "",
                    street_address: d.street_address || "",
                    location: d.location || { type: "Point", coordinates: [] },
                    lat: d.location?.coordinates?.[1] || "",
                    lng: d.location?.coordinates?.[0] || "",
                    quantity: d.quantity || "",
                    brands_nearby: d.brands_nearby?.length > 0
                        ? d.brands_nearby.map((b) => ({ name: b.name || "", image: b.image || b.image_url || "" }))
                        : [],
                    city: d.city?._id || d.city || "",
                    currency: d.currency || "",
                    starting_price: d.starting_price || "",
                    project_area: d.project_area || "",
                    area_unit: d.area_unit || "acres",
                    average_price: d.average_price || "",
                    average_rental: d.average_rental || "",
                    country: d.country?._id || d.country || "",
                    extra_benefits: d.extra_benefits || "",
                    hold_time: d.hold_time || 48,
                    discount_value: d.discount_value || "",
                    state: d.state?._id || d.state || "",
                    possesion_date: d.possesion_date ? d.possesion_date.split("T")[0] : "",
                    property_age: d.property_age || "",
                    no_of_towers: d.no_of_towers || "",
                    no_of_floors_resale: d.no_of_floors_resale || "",
                    no_of_plots: d.no_of_plots || "",
                    no_of_parks: d.no_of_parks || "",
                    green_area_percentage: d.green_area_percentage || "",
                    community: d.community || "",
                    key_project_details: d.key_project_details || [],
                    dubai_based_project_type: d.dubai_based_project_type || "",
                    about_project: d.about_project || "",
                    about_builder: d.about_builder || "",
                    note: d.note || "",
                    payment_note: d.payment_note || "",
                    brochure: d.brochure || "",
                    amenities: d.amenities?.map((a) => a._id || a) || [],
                    cover_photo: d.cover_photo || "",
                    images: d.images || [],
                    basement: d.basement || [],
                    terrace: d.terrace || [],
                    landmarks: d.landmarks || [],
                    localities: d.localities?.map((l) => l._id || l) || [],
                });

                if (d.cover_photo) setCoverImgPreview(d.cover_photo);
                if (d.images?.length > 0) setExistingProjectImgs(d.images);
                if (d.faqs?.length > 0) setFaqs(d.faqs);
                if (d.news?.length > 0) setNews(d.news);
                if (d.yt_links?.length > 0) setYtLinks(d.yt_links);
                if (d.project_highlight?.length > 0) setProjectHighlight(d.project_highlight);
                if (d.two_d_map?.length > 0) setTwoDMap(d.two_d_map);

                if (d.country?.country_name) setSelectedCountry(d.country.country_name);
                if (d.categories?.length > 0) setCategoryChange(d.categories.map((c) => c.name || ""));
                if (d.project_type?.name) setProjectType(d.project_type.name);

                if (d.localities?.length > 0) {
                    const ids = d.localities.map((l) => l._id || l);
                    setSelectedLocalityIds(ids);
                    setLocalitiesArray(d.localities.map((l) => ({ id: l._id || l, label: l.name || "" })));
                }
                const cityId = d.city?._id || d.city;
                if (cityId) {
                    dispatch(getLocalities({ city_id: cityId }));
                }
            } catch (err) {
                toast.error("Failed to load project");
            } finally {
                setLoading(false);
            }
        };
        if (id) fetchProject();
    }, [id]);

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(getEnums());
        dispatch(viewCountryListPagination());
        const payload = { page: 1, limit: 2000, business_type: ["resale"] };
        const builderPayload = { page: 1, limit: 2000, is_deleted: false };
        dispatch(viewAmenitiesList(payload));
        dispatch(viewCategoryList(payload));
        dispatch(viewBuilderList(builderPayload));
    }, [dispatch]);

    useEffect(() => {
        if (projectPayload.project_type)
            dispatch(viewSubCategoryList({ page: 1, limit: 2000, category_id: projectPayload.project_type }));
        if (projectPayload.country)
            dispatch(viewStateListPagination({ country: projectPayload.country }));
        if (projectPayload.state)
            dispatch(viewCityListPagination({ state: projectPayload.state, country: projectPayload.country, page: 1, limit: 1000 }));
    }, [dispatch, projectPayload.project_type, projectPayload.country, projectPayload.state]);

    useEffect(() => {
        const handler = (e) => {
            if (builderDropdownRef.current && !builderDropdownRef.current.contains(e.target)) {
                setBuilderDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const geocodedRef = useRef(false);
    useEffect(() => {
        if (!isLoaded) return;
        if (!projectPayload.lat || !projectPayload.lng) return;
        if (geocodedRef.current) return;

        const lat = Number(projectPayload.lat);
        const lng = Number(projectPayload.lng);
        if (isNaN(lat) || isNaN(lng)) return;
        geocodedRef.current = true;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
            { location: { lat, lng } },
            (results, status) => {
                console.log("Geocode status:", status);
                console.log("Geocode results:", results);
                if (status === "OK" && results.length > 0) {
                    const preferred = results.find(r =>
                        r.types.includes("locality") ||
                        r.types.includes("sublocality") ||
                        r.types.includes("neighborhood") ||
                        r.types.includes("administrative_area_level_2")
                    );
                    setMapAddressInput((preferred || results[0]).formatted_address);
                } else {
                    setMapAddressInput(projectPayload.street_address || "");
                }
            }
        );
    }, [isLoaded, projectPayload.lat, projectPayload.lng]);

    const communityData = useMemo(
        () => Object.entries(enumsData?.data?.plots_community || {}).map(([key, value]) => ({ label: key, value })),
        [enumsData?.data?.plots_community]
    );

    // ── Filtered amenities matching the selected project type (same as Add) ──
    const projectAmenities = Array.isArray(amenities?.data)
        ? amenities.data.filter(
            (amenity) =>
                amenity?.project_type?._id === projectPayload.project_type &&
                amenity.amenities_for === "project"
        )
        : [];

    const isCommercialCategory = useMemo(() => projectType.trim().toLowerCase() === "commercial", [projectType]);
    const isResidential = projectType.trim().toLowerCase() === "residential";
    const isPlotCategory = categoryChange[0]?.toLowerCase().replace(/\s/g, "") === "plot";
    const isVilla = categoryChange[0]?.toLowerCase().replace(/\s/g, "") === "villa";
    const isUAE = selectedCountry.toLowerCase().replace(/\s/g, "") === "unitedarabemirates";

    const areaUnitOptions = enumsData?.data?.AreaUnit
        ? Object.values(enumsData.data.AreaUnit).map((item) => ({ label: item, value: item }))
        : [{ label: "sq.ft", value: "sq.ft" }, { label: "acre", value: "acre" }, { label: "sq.m", value: "sq.m" }];

    const constructionStatusOptions = enumsData?.data?.ConstructionStatus
        ? Object.values(enumsData.data.ConstructionStatus).map((item) => ({ label: ToFirstCapital(item), value: item }))
        : [
            { label: "Under Construction", value: "Under Construction" },
            { label: "Ready to move", value: "Ready to move" },
            { label: "Not started", value: "Not started" },
        ];

    const onAutocompleteLoad = (autocomplete) => { autocompleteRef.current = autocomplete; };

    const onPlaceChanged = () => {
        if (!autocompleteRef.current) return;
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.geometry || !place.geometry.location) {
            toast.error("Please select a valid address from the dropdown.");
            return;
        }
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapAddressInput(place.formatted_address || "");
        setProjectPayload((prev) => ({
            ...prev,
            lat,
            lng,
            location: { type: "Point", coordinates: [lng, lat] },  // numbers not strings
        }));
    };

    const handleAddChange = useCallback(
        (e) => {
            const { name, value } = e.target;

            if (name === "no_of_towers") {
                if (value > 20) toast.error("The maximum no. of towers can be 20.");
                const capped = Math.min(20, Number(value) || 0);
                setProjectPayload((prev) => ({ ...prev, no_of_towers: Math.abs(capped) }));
                return;
            }

            if (name === "project_type") {
                const selectedCategory = categories?.data?.find((cat) => cat._id === value);
                setProjectType(selectedCategory?.name || "");
                setProjectPayload((prev) => ({ ...prev, project_type: value, categories: [], is_plot_project: false }));
                setCategoryChange([]);
                return;
            }

            if (name === "categories") {
                if (!value) return;

                const currentProjectType = categories?.data
                    ?.find((cat) => cat._id === projectPayload.project_type)
                    ?.name?.trim()
                    ?.toLowerCase() || "";

                if (currentProjectType === "residential") {
                    const catObj = subCategories?.data?.find((c) => c._id === value);
                    setCategoryChange([catObj?.name || ""]);
                    setProjectPayload((prev) => ({ ...prev, categories: [value] }));

                } else if (currentProjectType === "commercial") {
                    setProjectPayload((prev) => {
                        if (prev.categories.includes(value)) {
                            toast.error("Category already exists!");
                            return prev;
                        }
                        const catObj = subCategories?.data?.find((c) => c._id === value);
                        setCategoryChange((old) => [...old, catObj?.name || ""]);
                        return { ...prev, categories: [...prev.categories, value] };
                    });

                } else {
                    // Island, TEST12, or any other type
                    setProjectPayload((prev) => {
                        if (prev.categories.includes(value)) {
                            toast.error("Category already exists!");
                            return prev;
                        }
                        const catObj = subCategories?.data?.find((c) => c._id === value);
                        setCategoryChange((old) => [...old, catObj?.name || ""]);
                        return { ...prev, categories: [...prev.categories, value] };
                    });
                }
                return;
            }

            if (name === "key_project_details") {
                setProjectPayload((prev) => {
                    if (prev.key_project_details.includes(value)) return prev;
                    return { ...prev, key_project_details: [...prev.key_project_details, value] };
                });
                return;
            }

            if (name === "is_investment_opportunity" || name === "is_assured_return" || name === "is_assured_rental") {
                setProjectPayload((prev) => ({ ...prev, [name]: value === "true" || value === true }));
                return;
            }

            if (name === "country") {
                const countryObj = countryList?.data?.find((c) => c._id === value);
                setSelectedCountry(countryObj?.country_name || "");
                setProjectPayload((prev) => ({ ...prev, country: value }));
                return;
            }

            // ── Added: mirrors Add project handler ──
            if (name === "is_plot_project") {
                const val = value === "true" || value === true;
                setProjectPayload((prev) => ({ ...prev, is_plot_project: val, categories: [] }));
                setCategoryChange([]);
                return;
            }

            if (name === "city") {
                setSelectedLocalityIds([]);
                setLocalitiesArray([]);
                setLocalitySearch("");
                setProjectPayload((prev) => ({ ...prev, city: value, localities: [] }));
                if (value) dispatch(getLocalities({ city_id: value }));
                return;
            }

            setProjectPayload((prev) => ({ ...prev, [name]: value }));
        }, [projectType, cities, countryList, subCategories, categories, dispatch]
    );

    // Cover image
    const handleCoverChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setNewCoverImg(file);
        setCoverImgPreview(URL.createObjectURL(file));
    };
    const handleRemoveCoverImage = (e) => {
        e.preventDefault();
        setNewCoverImg(null);
        setCoverImgPreview("");
        setProjectPayload((prev) => ({ ...prev, cover_photo: "" }));
    };

    const handleRemoveHighlightImage = (index) => {
        const updated = [...projectHighlight];
        updated[index].image = "";
        setProjectHighlight(updated);
    };

    const handleRemoveNewsImage = (index) => {
        const updated = [...news];
        updated[index].image = "";
        setNews(updated);
    };

    // Project images
    const handleProjectImageChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const totalCount = existingProjectImgs.length + newProjectImgs.length + selectedFiles.length;
        if (totalCount > 25) { toast.error("You can only upload up to 25 images."); return; }
        setNewProjectImgs((prev) => [...prev, ...selectedFiles]);
        setNewProjectImgPreviews((prev) => [...prev, ...selectedFiles.map((f) => URL.createObjectURL(f))]);
    };
    const handleRemoveExistingImage = (index) => {
        setExistingProjectImgs((prev) => prev.filter((_, i) => i !== index));
    };
    const handleRemoveNewImage = (index) => {
        setNewProjectImgs((prev) => prev.filter((_, i) => i !== index));
        setNewProjectImgPreviews((prev) => prev.filter((_, i) => i !== index));
    };

    const handleBrochureUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.type !== "application/pdf") { toast.error("Only PDF files are allowed for brochure."); return; }
        setBrochureUploading(true);
        const responseUrl = await AddImage(file);
        setBrochureUploading(false);
        if (responseUrl) { setProjectPayload((prev) => ({ ...prev, brochure: responseUrl })); toast.success("Brochure uploaded!"); }
    };

    const handleTwoDMapChange = (index, field, value) =>
        setTwoDMap((prev) => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
    const addTwoDMap = () => setTwoDMap((prev) => [...prev, { title: "", tower_name: "", image: "" }]);
    const removeTwoDMap = (index) => setTwoDMap((prev) => prev.filter((_, i) => i !== index));
    const handleTwoDMapImageUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;

        const isPdf = file.type === "application/pdf";
        const url = await AddImage(file);

        if (url) {
            handleTwoDMapChange(index, "image", url);
            handleTwoDMapChange(index, "isPdf", isPdf);
        }
    };

    const handleFaqChange = (index, field, value) =>
        setFaqs((prev) => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
    const addFaq = () => setFaqs((prev) => [...prev, { question: "", answer: "" }]);
    const removeFaq = (index) => setFaqs((prev) => prev.filter((_, i) => i !== index));

    const handleNewsChange = (index, field, value) =>
        setNews((prev) => prev.map((n, i) => i === index ? { ...n, [field]: value } : n));
    const addNews = () => setNews((prev) => [...prev, { title: "", description: "", image: "" }]);
    const removeNews = (index) => setNews((prev) => prev.filter((_, i) => i !== index));
    const handleNewsImageUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;
        const url = await AddImage(file);
        if (url) handleNewsChange(index, "image", url);
    };

    const handleYtChange = (index, value) =>
        setYtLinks((prev) => prev.map((y, i) => i === index ? { ...y, url: value } : y));
    const addYtLink = () => setYtLinks((prev) => [...prev, { index: prev.length, url: "" }]);
    const removeYtLink = (index) => setYtLinks((prev) => prev.filter((_, i) => i !== index));

    const handleHighlightChange = (index, field, value) =>
        setProjectHighlight((prev) => prev.map((h, i) => i === index ? { ...h, [field]: value } : h));
    const addHighlight = () => setProjectHighlight((prev) => [...prev, { title: "", description: "", image: "" }]);
    const removeHighlight = (index) => setProjectHighlight((prev) => prev.filter((_, i) => i !== index));
    const handleHighlightImageUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;
        const url = await AddImage(file);
        if (url) handleHighlightChange(index, "image", url);
    };

    const majorLocalities = localities?.data?.major || [];
    const minorLocalities = localities?.data?.minor || [];
    const localitiesList = [...majorLocalities, ...minorLocalities];

    const filteredMajor = majorLocalities.filter(
        (loc) =>
            loc.name?.toLowerCase().includes(localitySearch.toLowerCase()) ||
            loc.citydata?.cityname?.toLowerCase().includes(localitySearch.toLowerCase())
    );
    const filteredMinor = minorLocalities.filter(
        (loc) =>
            loc.name?.toLowerCase().includes(localitySearch.toLowerCase()) ||
            loc.citydata?.cityname?.toLowerCase().includes(localitySearch.toLowerCase())
    );

    const toggleLocalitySelection = (locality) => {
        const id = locality._id;
        setSelectedLocalityIds((prevIds) => {
            const alreadySelected = prevIds.includes(id);
            if (!alreadySelected && prevIds.length >= 10) {
                toast.error("You can select a maximum of 10 localities.");
                return prevIds;
            }
            const updatedIds = alreadySelected
                ? prevIds.filter((item) => item !== id)
                : [...prevIds, id];

            setProjectPayload((prev) => ({ ...prev, localities: updatedIds }));

            if (alreadySelected) {
                setLocalitiesArray((prev) => prev.filter((item) => item.id !== id));
            } else {
                const matched = localitiesList.find((loc) => loc._id === id);
                setLocalitiesArray((prev) => [
                    ...prev,
                    { id, label: matched?.name || matched?.formattedaddress || "" }
                ]);
            }

            return updatedIds;
        });
    };

    const handleRemoveCategory = (index) => {
        setProjectPayload((prev) => ({ ...prev, categories: prev.categories.filter((_, i) => i !== index) }));
        setCategoryChange((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;

        if (!projectPayload.project_name.trim()) return toast.error("Project Name is required!");
        if (projectPayload.categories.length === 0) return toast.error("Please select at least one category.");
        if (!projectPayload.country) return toast.error("Please select a country.");
        if (!projectPayload.state) return toast.error("Please select a state.");
        if (!projectPayload.city) return toast.error("Please select a city.");
        if (!projectPayload.project_type) return toast.error("Please select a project type.");
        if (!projectPayload.construction_status) return toast.error("Please select a construction status.");
        if (projectPayload.hold_time < 48 || projectPayload.hold_time === "") return toast.error("Hold Time should be >= 48 hours.");
        if (!projectPayload.street_address?.trim()) return toast.error("Address is required!");
        const plainText = projectPayload.about_project?.replace(/<[^>]*>/g, "").trim();
        if (!plainText) return toast.error("About Project is required!");
        if (!projectPayload.location) return toast.error("Project Address is required!");
        const validFaqs = faqs.filter((f) => f.question.trim() && f.answer.trim());
        if (validFaqs.length === 0) return toast.error("At least one FAQ is required!");

        if (!coverImgPreview) return toast.error("Cover photo is required!");

        const totalImages = existingProjectImgs.length + newProjectImgs.length;
        if (totalImages === 0) return toast.error("At least one project image is required!");

        setIsSubmitting(true);
        const uploadToast = toast.loading("Uploading images, please wait...");

        try {
            let coverPhotoUrl = projectPayload.cover_photo;
            if (newCoverImg) coverPhotoUrl = await AddImage(newCoverImg);

            const uploadedNewImgs = [];
            for (let i = 0; i < newProjectImgs.length; i++) {
                const url = await AddImage(newProjectImgs[i]);
                if (url) uploadedNewImgs.push({ url, index: existingProjectImgs.length + i + 1 });
            }
            const allImages = [...existingProjectImgs, ...uploadedNewImgs];

            toast.loading("Saving project...", { id: uploadToast });

            const cleanFaqs = faqs.filter((f) => f.question.trim() && f.answer.trim());
            const cleanNews = news.filter((n) => n.title.trim());
            const cleanYtLinks = ytLinks.filter((y) => y.url.trim());
            const cleanHighlights = projectHighlight.filter((h) => h.title.trim());

            const isEmptyValue = (value) => {
                if (value === "" || value === undefined || value === null || (typeof value === "number" && isNaN(value))) return true;
                if (Array.isArray(value)) {
                    return value.length === 0 || value.every(
                        (item) => typeof item === "object" && item !== null &&
                            Object.values(item).every((val) => val === "" || val === null || val === undefined)
                    );
                }
                if (typeof value === "object" && value !== null && "coordinates" in value) {
                    return !value.coordinates || value.coordinates.length === 0;
                }
                return false;
            };

            const patchedPayload = {
                ...projectPayload,
                cover_photo: coverPhotoUrl,
                images: allImages,
                brands_nearby: projectPayload.brands_nearby.map((b) => ({
                    brand_id: b.id || b.brand_id || b._id || "",
                    name: b.name || "",
                    image: b.image || b.image_url || "",
                })),
                area_unit: projectPayload.project_area > 0 && !projectPayload.area_unit ? "acre" : projectPayload.area_unit,
                faqs: cleanFaqs,
                news: cleanNews.length > 0 ? cleanNews : undefined,
                two_d_map: twoDMap.filter((m) => m.image).length > 0 ? twoDMap.filter((m) => m.image) : undefined,
                yt_links: cleanYtLinks.length > 0 ? cleanYtLinks : undefined,
                project_highlight: cleanHighlights.length > 0 ? cleanHighlights : undefined,
            };

            const { lat, lng, ...payloadWithoutLatLng } = patchedPayload;

            const filteredPayload = Object.fromEntries(
                Object.entries(payloadWithoutLatLng).filter(([_, value]) => !isEmptyValue(value))
            );

            const response = await dispatch(updateProjectResale(id, filteredPayload));

            if (response?.statusCode === 200) {
                toast.success("Project updated successfully!", { id: uploadToast });
                navigate("/resale_project");
            } else {
                toast.error(response?.message || "Something went wrong.", { id: uploadToast });
            }
        } catch (error) {
            toast.error("Something went wrong. Please try again.", { id: uploadToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="erp-loading">
                    <div className="erp-loading-spinner" />
                    <p>Loading project...</p>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="erp-wrapper">
                <div className="erp-header">
                    <div className="erp-header-left">
                        <div className="erp-header-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="erp-title">Edit Resale Project</h1>
                            <p className="erp-subtitle">Update the project details below</p>
                        </div>
                    </div>
                    <button className="erp-view-btn" type="button" onClick={() => navigate("/resale_project")}>View Projects</button>
                </div>

                <form onSubmit={handleSubmit} className="erp-form">

                    {/* ── 01 BUILDER & PROJECT INFO ───────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">01</span>
                            <h2 className="erp-card-title">Builder & Project Info</h2>
                        </div>
                        <div className="erp-grid erp-grid-2">
                            <div className="erp-field">
                                <label className="erp-label">Select Builder</label>
                                <div ref={builderDropdownRef} style={{ position: "relative" }}>
                                    <div
                                        className="erp-input"
                                        style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", height: 42, padding: "0 12px" }}
                                        onClick={() => setBuilderDropdownOpen((prev) => !prev)}
                                    >
                                        {projectPayload.builder ? (() => {
                                            const selected = builders?.data?.find((b) => b._id === projectPayload.builder);
                                            return selected ? (
                                                <>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                                                        {selected.brand_logo && (
                                                            <img src={selected.brand_logo} alt="" style={{ width: 26, height: 26, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                                                        )}
                                                        <span style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap" }}>{selected.brand_name}</span>
                                                        <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: "auto", marginRight: 8, whiteSpace: "nowrap" }}>{selected.builder_id}</span>
                                                        <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", whiteSpace: "nowrap", textTransform: "capitalize" }}>
                                                            {Array.isArray(selected.business_type) ? selected.business_type[0] : selected.business_type || ""}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); setProjectPayload((prev) => ({ ...prev, builder: "" })); }}
                                                        style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 18, lineHeight: 1, padding: "0 4px", flexShrink: 0 }}
                                                    >×</button>
                                                </>
                                            ) : (
                                                <span style={{ color: "#9ca3af" }}>Select Builder</span>
                                            );
                                        })() : (
                                            <span style={{ color: "#9ca3af" }}>Select Builder</span>
                                        )}
                                    </div>

                                    {builderDropdownOpen && (
                                        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200, background: "#fff", border: "1.5px solid #e4e7ec", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,.1)", maxHeight: 260, overflowY: "auto" }}>
                                            <div style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                                                <input
                                                    className="erp-input"
                                                    type="text"
                                                    placeholder="Search builder..."
                                                    value={builderSearch}
                                                    onChange={(e) => setBuilderSearch(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    autoFocus
                                                    style={{ height: 34, fontSize: 13 }}
                                                />
                                            </div>
                                            {Array.isArray(builders?.data) && builders.data
                                                .filter((b) => b.brand_name?.toLowerCase().includes(builderSearch.toLowerCase()))
                                                .map((b) => (
                                                    <div
                                                        key={b._id}
                                                        onClick={() => { setProjectPayload((prev) => ({ ...prev, builder: b._id })); setBuilderDropdownOpen(false); setBuilderSearch(""); }}
                                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f3f4f6", transition: "background 150ms" }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = ""}
                                                    >
                                                        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                                            {b.brand_logo && (
                                                                <img src={b.brand_logo} alt="" style={{ width: 30, height: 30, borderRadius: 6, objectFit: "cover", border: "1px solid #e4e7ec", flexShrink: 0 }} />
                                                            )}
                                                            <div style={{ minWidth: 0 }}>
                                                                <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>{b.brand_name}</p>
                                                                <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{b.builder_id}</p>
                                                            </div>
                                                        </div>
                                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", whiteSpace: "nowrap", marginLeft: 12, textTransform: "capitalize" }}>
                                                            {Array.isArray(b.business_type) ? b.business_type[0] : b.business_type || ""}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="erp-field">
                                <label className="erp-label">Project Name <span className="erp-req">*</span></label>
                                <input className="erp-input" type="text" name="project_name" value={projectPayload.project_name} onChange={handleAddChange} placeholder="Enter Project Name" />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">RERA ID</label>
                                <input className="erp-input" type="text" name="rera_id" value={projectPayload.rera_id} onChange={handleAddChange} placeholder="Enter RERA ID (optional)" />
                            </div>
                            {/* ── Added: Business Type (was missing in Edit) ── */}
                            <div className="erp-field">
                                <label className="erp-label">Business Type <span className="erp-req">*</span></label>
                                <select className="erp-select" name="business_type" value={projectPayload.business_type} onChange={handleAddChange}>
                                    <option value="resale">Resale</option>
                                    {/* <option value="fresh">Fresh</option>
                                    <option value="business">Business</option> */}
                                    <option value="rental">Rental</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* ── 02 PROJECT DETAILS ──────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">02</span>
                            <h2 className="erp-card-title">Project Details</h2>
                        </div>
                        <div className="erp-grid erp-grid-2">
                            <div className="erp-field">
                                <label className="erp-label">Is Investment Opportunity?</label>
                                <select className="erp-select" name="is_investment_opportunity" value={projectPayload.is_investment_opportunity} onChange={handleAddChange}>
                                    <option value="">Select an Option</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            {projectPayload.is_investment_opportunity && (
                                <>
                                    <div className="erp-field">
                                        <label className="erp-label">Assured Return?</label>
                                        <select className="erp-select" name="is_assured_return" value={projectPayload.is_assured_return} onChange={handleAddChange}>
                                            <option value="">Select an Option</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                    </div>
                                    <div className="erp-field">
                                        <label className="erp-label">Assured Rental?</label>
                                        <select className="erp-select" name="is_assured_rental" value={projectPayload.is_assured_rental} onChange={handleAddChange}>
                                            <option value="">Select an Option</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                    </div>
                                    {/* ── Added: Investment Features button (was missing in Edit) ── */}
                                    <div className="erp-field erp-field--center">
                                        <label className="erp-label">Investment Features</label>
                                        <button className="erp-furnish-btn erp-furnish-btn--all" type="button" onClick={() => setOpenInvestmentModal(true)}>
                                            {trueCount === 0 ? "Add Features" : `${trueCount} Selected`}
                                        </button>
                                    </div>
                                </>
                            )}

                            <div className="erp-field">
                                <label className="erp-label">Project Type <span className="erp-req">*</span></label>
                                <select className="erp-select" name="project_type" value={projectPayload.project_type} onChange={handleAddChange}>
                                    <option value="">Select Project Type</option>
                                    {Array.isArray(categories?.data) && categories.data.map((cat) => (
                                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>

                            {isCommercialCategory && (
                                <div className="erp-field">
                                    <label className="erp-label">Commercial Plot Project?</label>
                                    <select className="erp-select" name="is_plot_project" value={projectPayload.is_plot_project} onChange={handleAddChange}>
                                        <option value="">Select an Option</option>
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </select>
                                </div>
                            )}

                            <div className="erp-field">
                                <label className="erp-label">Category <span className="erp-req">*</span></label>
                                <select className="erp-select" name="categories" value="" disabled={!projectPayload.project_type} onChange={handleAddChange}>
                                    <option value="">Select Category</option>
                                    {Array.isArray(subCategories?.data) &&
                                        (projectPayload.is_plot_project === "true" || projectPayload.is_plot_project === true
                                            ? subCategories.data.filter((cat) => cat.name.toLowerCase().replace(/\s/g, "").includes("plot"))
                                            : subCategories.data
                                        )
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map((cat) => (
                                                <option key={cat._id} value={cat._id}>{cat.name}</option>
                                            ))
                                    }
                                </select>
                                <div className="erp-tags-row">
                                    {categoryChange.map((label, i) => (
                                        <span key={i} className="erp-tag">
                                            {label}
                                            <button type="button" className="erp-tag-remove" onClick={() => handleRemoveCategory(i)}>×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {!(isPlotCategory || projectPayload.is_plot_project) && (
                                <div className="erp-field">
                                    <label className="erp-label">Construction Status</label>
                                    <select className="erp-select" name="construction_status" value={projectPayload.construction_status} onChange={handleAddChange}>
                                        <option value="">Select Status</option>
                                        {constructionStatusOptions.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {projectPayload.construction_status === "Ready to move" && (
                                <div className="erp-field">
                                    <label className="erp-label">Age of Property</label>
                                    <input className="erp-input" type="number" name="property_age" value={projectPayload.property_age} onChange={handleAddChange} placeholder="Enter Age" min={0} />
                                </div>
                            )}

                            <div className="erp-field">
                                <label className="erp-label">Launch Date</label>
                                <input className="erp-input" type="date" name="launch_date" value={projectPayload.launch_date} onChange={handleAddChange} max={projectPayload.possesion_date || ""} />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Possession Date</label>
                                <input className="erp-input" type="date" name="possesion_date" value={projectPayload.possesion_date} onChange={handleAddChange} min={projectPayload.launch_date || ""} disabled={!projectPayload.launch_date} />
                            </div>
                        </div>
                    </div>

                    {/* ── 03 AREA & PRICING ───────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">03</span>
                            <h2 className="erp-card-title">Area & Pricing</h2>
                        </div>
                        <div className="erp-grid erp-grid-2">
                            <div className="erp-field">
                                <label className="erp-label">Project Area</label>
                                <input className="erp-input" type="number" name="project_area" onWheel={(e) => e.target.blur()} value={projectPayload.project_area} onChange={handleAddChange} placeholder="Enter Area" />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Area Unit</label>
                                <select className="erp-select" name="area_unit" value={projectPayload.area_unit} onChange={handleAddChange} disabled={!projectPayload.project_area}>
                                    <option value="">Select Unit</option>
                                    {areaUnitOptions.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
                                </select>
                            </div>

                            {isResidential && !isPlotCategory && (
                                <div className="erp-field">
                                    <label className="erp-label">No. of Towers</label>
                                    <input className="erp-input" type="number" name="no_of_towers" onWheel={(e) => e.target.blur()} value={projectPayload.no_of_towers} onChange={handleAddChange} placeholder="Max 20" min={0} max={20} />
                                </div>
                            )}

                            {!isPlotCategory && (
                                <div className="erp-field">
                                    <label className="erp-label">Total Floors</label>
                                    <input className="erp-input" type="number" name="no_of_floors_resale" onWheel={(e) => e.target.blur()} value={projectPayload.no_of_floors_resale} onChange={handleAddChange} placeholder="Enter total no. of floors" min={0} />
                                </div>
                            )}

                            {!isPlotCategory && (
                                <div className="erp-field">
                                    <label className="erp-label">Terrace Accessible?</label>
                                    <select className="erp-select" name="has_terrace" value={projectPayload.has_terrace || ""} onChange={handleAddChange}>
                                        <option value="">Select an Option</option>
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </select>

                                    {(projectPayload.has_terrace === "true" || projectPayload.has_terrace === true) && (
                                        <div style={{ marginTop: 10 }}>
                                            <div style={{ display: "flex", gap: 8 }}>
                                                <input
                                                    className="erp-input"
                                                    type="text"
                                                    placeholder="Enter terrace detail and press Add"
                                                    value={terraceInput}
                                                    onChange={(e) => setTerraceInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            if (!terraceInput.trim()) return;
                                                            setProjectPayload((prev) => ({ ...prev, terrace: [...prev.terrace, terraceInput.trim()] }));
                                                            setTerraceInput("");
                                                        }
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="erp-furnish-btn"
                                                    onClick={() => {
                                                        if (!terraceInput.trim()) return;
                                                        setProjectPayload((prev) => ({ ...prev, terrace: [...prev.terrace, terraceInput.trim()] }));
                                                        setTerraceInput("");
                                                    }}
                                                >
                                                    Add
                                                </button>
                                            </div>
                                            {projectPayload.terrace.length > 0 && (
                                                <div className="erp-tags-row" style={{ marginTop: 8 }}>
                                                    {projectPayload.terrace.map((item, i) => (
                                                        <span key={i} className="erp-tag">
                                                            {item}
                                                            <button type="button" className="erp-tag-remove"
                                                                onClick={() => setProjectPayload((prev) => ({ ...prev, terrace: prev.terrace.filter((_, idx) => idx !== i) }))}>×</button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {isPlotCategory && (
                                <div className="erp-field">
                                    <label className="erp-label">Total No. of Plots</label>
                                    <input className="erp-input" type="number" name="no_of_plots" onWheel={(e) => e.target.blur()} value={projectPayload.no_of_plots} onChange={handleAddChange} placeholder="Enter Total Plots" />
                                </div>
                            )}

                            {isResidential && (
                                <>
                                    <div className="erp-field">
                                        <label className="erp-label">No. of Parks</label>
                                        <input className="erp-input" type="number" name="no_of_parks" onWheel={(e) => e.target.blur()} value={projectPayload.no_of_parks} onChange={handleAddChange} placeholder="Enter no. of parks" />
                                    </div>
                                    <div className="erp-field">
                                        <label className="erp-label">Green Area Percentage</label>
                                        <input className="erp-input" type="number" name="green_area_percentage" onWheel={(e) => e.target.blur()} value={projectPayload.green_area_percentage} onChange={handleAddChange} placeholder="Enter % of green area" />
                                    </div>
                                </>
                            )}

                            {isResidential && !isPlotCategory && (
                                <>
                                    <div className="erp-field">
                                        <label className="erp-label">Clubhouse Area</label>
                                        <input className="erp-input" type="number" name="clubhouse_area" onWheel={(e) => e.target.blur()} value={projectPayload.clubhouse_area} onChange={handleAddChange} placeholder="Enter Clubhouse Area" />
                                    </div>
                                    <div className="erp-field">
                                        <label className="erp-label">Clubhouse Area Unit</label>
                                        <select className="erp-select" name="clubhouse_area_measure_in" value={projectPayload.clubhouse_area_measure_in} onChange={handleAddChange}>
                                            {areaUnitOptions.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className="erp-field">
                                <label className="erp-label">Currency</label>
                                <select className="erp-select" name="currency" value={projectPayload.currency || "INR"} onChange={handleAddChange}>
                                    <option value="INR">INR (₹)</option>
                                    <option value="USD">USD ($)</option>
                                    <option value="AED">AED (د.إ)</option>
                                    <option value="EUR">EUR (€)</option>
                                </select>
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Starting Price</label>
                                <input className="erp-input" type="number" name="starting_price" onWheel={(e) => e.target.blur()} value={projectPayload.starting_price} onChange={handleAddChange} placeholder="Enter Price" min={0} />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Average Price (per sq.ft)</label>
                                <input className="erp-input" type="number" name="average_price" onWheel={(e) => e.target.blur()} value={projectPayload.average_price} onChange={handleAddChange} placeholder="Enter Avg. Price" />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Average Rental</label>
                                <input className="erp-input" type="number" name="average_rental" onWheel={(e) => e.target.blur()} value={projectPayload.average_rental} onChange={handleAddChange} placeholder="Enter avg. rental yield" />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Minimum Hold Time (hours) <span className="erp-req">*</span></label>
                                <input className="erp-input" type="number" name="hold_time" value={projectPayload.hold_time} onChange={handleAddChange} placeholder="default: 48 hours" />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Maximum Discount Value (%)</label>
                                <input className="erp-input" type="number" name="discount_value" value={projectPayload.discount_value} onChange={handleAddChange} placeholder="Enter value ( in % )" max={100} step="any" />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Extra Benefits</label>
                                <input className="erp-input" type="text" name="extra_benefits" value={projectPayload.extra_benefits} onChange={handleAddChange} placeholder="Any additional benefits" />
                            </div>

                            {(isPlotCategory || isVilla) && (
                                <div className="erp-field">
                                    <label className="erp-label">Community</label>
                                    <select className="erp-select" name="community" value={projectPayload.community} onChange={handleAddChange}>
                                        <option value="">Select Community</option>
                                        {communityData.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                                    </select>
                                </div>
                            )}

                            {isVilla && (
                                <div className="erp-field">
                                    <label className="erp-label">Key Project Details</label>
                                    <select className="erp-select" name="key_project_details" value="" onChange={handleAddChange}>
                                        <option value="">Select Key Details</option>
                                        {enumsData?.data?.key_project_details?.map((item) => (
                                            <option key={item} value={item}>{item}</option>
                                        ))}
                                    </select>
                                    <div className="erp-tags-row">
                                        {projectPayload.key_project_details.map((detail, i) => (
                                            <span key={i} className="erp-tag">
                                                {detail}
                                                <button type="button" className="erp-tag-remove"
                                                    onClick={() => setProjectPayload((prev) => ({ ...prev, key_project_details: prev.key_project_details.filter((_, idx) => idx !== i) }))}>×</button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── 04 LOCATION ─────────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">04</span>
                            <h2 className="erp-card-title">Location</h2>
                        </div>
                        <div className="erp-grid erp-grid-2">
                            <div className="erp-field">
                                <label className="erp-label">Country <span className="erp-req">*</span></label>
                                <select className="erp-select" name="country" value={projectPayload.country} onChange={handleAddChange}>
                                    <option value="">Select Country</option>
                                    {Array.isArray(countryList?.data) && countryList.data.map((c) => (
                                        <option key={c._id} value={c._id}>{c.country_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">State <span className="erp-req">*</span></label>
                                <select className="erp-select" name="state" value={projectPayload.state} onChange={handleAddChange} disabled={!projectPayload.country}>
                                    <option value="">Select State</option>
                                    {Array.isArray(states?.data) && states.data.map((s) => (
                                        <option key={s._id} value={s._id}>{s.state_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">City <span className="erp-req">*</span></label>
                                <select className="erp-select" name="city" value={projectPayload.city} onChange={handleAddChange} disabled={!projectPayload.state}>
                                    <option value="">Select City</option>
                                    {cities.map((c) => (
                                        <option key={c._id} value={c._id}>{c.city_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="erp-field">
                                <label className="erp-label">
                                    Localities (Max 10)
                                    {projectPayload.localities.length > 0 && (
                                        <span className="erp-multi-count">{projectPayload.localities.length} selected</span>
                                    )}
                                </label>
                                <div className="erp-input" style={{ minHeight: "44px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: projectPayload.city ? "pointer" : "not-allowed" }}
                                    onClick={() => {
                                        if (!projectPayload.city) { toast.error("Please select city first"); return; }
                                        setLocalitiesPopup(true);
                                    }}>
                                    <span>{projectPayload.localities?.length > 0 ? `${projectPayload.localities.length} localities selected` : "Select localities"}</span>
                                    <span>+</span>
                                </div>
                                {localitiesArray.length > 0 && (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
                                        {localitiesArray.map((loc) => (
                                            <div key={loc.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: "20px", background: "#f3f4f6", fontSize: "13px", color: "#111827" }}>
                                                {loc.label}
                                                <button
                                                    type="button"
                                                    onClick={() => toggleLocalitySelection({ _id: loc.id })}
                                                    style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 15, lineHeight: 1, padding: 0, display: "flex", alignItems: "center" }}
                                                >×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {localitiesPopup && (
                                <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: "90%", maxWidth: 600, maxHeight: "80vh", display: "flex", flexDirection: "column", gap: 12 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Select Localities ({selectedLocalityIds.length}/10)</h3>
                                            <button className="erp-btn erp-btn--cancel" type="button" onClick={() => setLocalitiesPopup(false)}>Done</button>
                                        </div>
                                        <input className="erp-input" type="text" placeholder="Search localities..." value={localitySearch} onChange={(e) => setLocalitySearch(e.target.value)} />
                                        <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                                            {localitiesLoading ? (
                                                <p style={{ textAlign: "center", color: "#6b7280", padding: "20px 0" }}>Loading localities...</p>
                                            ) : filteredMajor.length === 0 && filteredMinor.length === 0 ? (
                                                <p style={{ textAlign: "center", color: "#6b7280", padding: "20px 0" }}>No localities found.</p>
                                            ) : (
                                                <>
                                                    {filteredMajor.length > 0 && (
                                                        <>
                                                            <p style={{ fontSize: 12, fontWeight: 600, color: "#8b5cf6", margin: "4px 0" }}>Major Localities</p>
                                                            {filteredMajor.map((locality) => {
                                                                const isSelected = selectedLocalityIds.includes(locality._id);
                                                                return (
                                                                    <div key={locality._id} onClick={() => toggleLocalitySelection(locality)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, cursor: "pointer", border: isSelected ? "2px solid #8b5cf6" : "1px solid #e5e7eb", background: isSelected ? "#f5f3ff" : "#fafafa" }}>
                                                                        <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, border: isSelected ? "2px solid #8b5cf6" : "2px solid #d1d5db", background: isSelected ? "#8b5cf6" : "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                            {isSelected && <span style={{ color: "white", fontSize: 13 }}>✓</span>}
                                                                        </div>
                                                                        <div>
                                                                            <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#111827" }}>{locality.name}</p>
                                                                            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>{locality.short_address || ""}</p>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </>
                                                    )}

                                                    {filteredMinor.length > 0 && (
                                                        <>
                                                            <p style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", margin: "10px 0 4px" }}>Other Localities</p>
                                                            {filteredMinor.map((locality) => {
                                                                const isSelected = selectedLocalityIds.includes(locality._id);
                                                                return (
                                                                    <div key={locality._id} onClick={() => toggleLocalitySelection(locality)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, cursor: "pointer", border: isSelected ? "2px solid #8b5cf6" : "1px solid #e5e7eb", background: isSelected ? "#f5f3ff" : "#fafafa" }}>
                                                                        <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, border: isSelected ? "2px solid #8b5cf6" : "2px solid #d1d5db", background: isSelected ? "#8b5cf6" : "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                            {isSelected && <span style={{ color: "white", fontSize: 13 }}>✓</span>}
                                                                        </div>
                                                                        <div>
                                                                            <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#111827" }}>{locality.name}</p>
                                                                            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>{locality.short_address || ""}</p>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isUAE && (
                                <div className="erp-field">
                                    <label className="erp-label">Type of Project (UAE)</label>
                                    <select className="erp-select" name="dubai_based_project_type" value={projectPayload.dubai_based_project_type} onChange={handleAddChange}>
                                        <option value="">Select Type</option>
                                        <option value="Off Plan">Off-Plan</option>
                                        <option value="Ready to Move">Ready to Move</option>
                                        <option value="Resale">Resale</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="erp-field" style={{ marginTop: 18 }}>
                            <label className="erp-label">Address <span className="erp-req">*</span></label>
                            <input className="erp-input" type="text" name="street_address" value={projectPayload.street_address || ""} onChange={handleAddChange} placeholder="Enter Project Address" />
                        </div>

                        <div className="erp-field" style={{ marginTop: 18 }}>
                            <label className="erp-label">Project Address (Map Search)<span className="acp-req">*</span></label>
                            {loadError && <input className="erp-input" type="text" placeholder="Enter address" />}
                            {!isLoaded && !loadError && <input className="erp-input" type="text" placeholder="Loading Google address search..." disabled />}
                            {isLoaded && !loadError && (
                                <Autocomplete
                                    onLoad={onAutocompleteLoad}
                                    onPlaceChanged={onPlaceChanged}
                                    fields={["geometry", "formatted_address", "name"]}
                                >
                                    <input
                                        className="erp-input"
                                        type="text"
                                        placeholder="Search address"
                                        disabled={!projectPayload.city}
                                        value={mapAddressInput}
                                        onChange={(e) => setMapAddressInput(e.target.value)}
                                    />
                                </Autocomplete>
                            )}
                        </div>
                    </div>

                    {/* ── 05 2D MAP ───────────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">05</span>
                            <h2 className="erp-card-title">2D Map (Optional)</h2>
                        </div>
                        {twoDMap.map((map, index) => (
                            <div key={index} style={{ background: "#f9fafb", border: "1px solid #e4e7ec", borderRadius: 12, padding: 18, marginBottom: 14 }}>
                                <div className="erp-grid erp-grid-2" style={{ marginBottom: 10 }}>
                                    <div className="erp-field">
                                        <label className="erp-label">Tower Name</label>
                                        <input className="erp-input" type="text" placeholder="Enter tower name (optional)" value={map.tower_name} onChange={(e) => handleTwoDMapChange(index, "tower_name", e.target.value)} />
                                    </div>
                                    <div className="erp-field">
                                        <label className="erp-label">Title</label>
                                        <input className="erp-input" type="text" placeholder="Enter title" value={map.title} onChange={(e) => handleTwoDMapChange(index, "title", e.target.value)} />
                                    </div>
                                    <div className="erp-field">
                                        <label className="erp-label">Map Image / Pdf</label>
                                        <input
                                            className="erp-input"
                                            type="file"
                                            accept="image/*,application/pdf"
                                            onChange={(e) => handleTwoDMapImageUpload(e, index)}
                                        />
                                        {map.image && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                                                {map.isPdf
                                                    ? <a href={map.image} target="_blank" rel="noreferrer">📄 View PDF</a>
                                                    : <img src={map.image} alt="2d-map" style={{ height: 60, borderRadius: 8, objectFit: "contain" }} />
                                                }
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        handleTwoDMapChange(index, "image", "");
                                                        handleTwoDMapChange(index, "isPdf", false);
                                                    }}
                                                    style={{ margin: "0px 0px 31px -35px", background: "transparent", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                        <path d="M10 11v6M14 11v6" />
                                                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {twoDMap.length > 1 && (
                                    <button type="button" className="erp-furnish-btn erp-furnish-btn--clear" onClick={() => removeTwoDMap(index)}>Remove</button>
                                )}
                            </div>
                        ))}
                        <button type="button" className="erp-furnish-btn" onClick={addTwoDMap}>+ Add Map</button>
                    </div>

                    {/* ── 06 COMMERCIAL INFO ──────────────────────────────────────────────── */}
                    {isCommercialCategory && (
                        <div className="erp-card">
                            <div className="erp-card-header">
                                <span className="erp-card-badge">06</span>
                                <h2 className="erp-card-title">Commercial Info</h2>
                            </div>
                            <div className="erp-field" style={{ marginBottom: 18 }}>
                                <label className="erp-label">Units in Project</label>
                                <input className="erp-input" type="number" name="quantity" value={projectPayload.quantity} onChange={handleAddChange} placeholder="Enter Units" min={0} />
                            </div>
                        </div>
                    )}

                    {/* ── 07 AMENITIES ── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">07</span>
                            <h2 className="erp-card-title">Amenities</h2>
                        </div>

                        {!projectPayload.project_type ? (
                            <div style={{ padding: "20px 0", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
                                Please select a Project Type first to view available amenities.
                            </div>
                        ) : projectAmenities.length === 0 ? (
                            <div style={{ padding: "20px 0", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
                                No amenities found for the selected project type.
                            </div>
                        ) : (
                            <>
                                {/* Search */}
                                <div style={{ position: "relative", marginBottom: 14 }}>
                                    <svg
                                        style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }}
                                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                    >
                                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search sub-amenities..."
                                        value={subAmenitySearch}
                                        onChange={(e) => setSubAmenitySearch(e.target.value)}
                                        style={{
                                            width: "100%",
                                            padding: "8px 10px 8px 32px",
                                            borderRadius: 10,
                                            border: "1.5px solid #e5e7eb",
                                            fontSize: 13,
                                            outline: "none",
                                            background: "#fafafa",
                                            boxSizing: "border-box",
                                        }}
                                    />
                                    {subAmenitySearch && (
                                        <button
                                            type="button"
                                            onClick={() => setSubAmenitySearch("")}
                                            style={{
                                                position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                                                background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 16, lineHeight: 1
                                            }}
                                        >×</button>
                                    )}
                                </div>

                                {/* If searching — flat sub-amenity list */}
                                {subAmenitySearch.trim() ? (
                                    <>
                                        {(() => {
                                            const allSubs = projectAmenities.flatMap(amenity =>
                                                (amenity.sub_amenities || [])
                                                    .filter(sub => !sub.is_deleted)
                                                    .filter(sub => sub.name?.toLowerCase().includes(subAmenitySearch.toLowerCase()))
                                                    .map(sub => ({ ...sub, parentName: amenity.name }))
                                            );

                                            return allSubs.length === 0 ? (
                                                <div style={{ textAlign: "center", color: "#9ca3af", padding: "20px 0", fontSize: 14 }}>
                                                    No sub-amenities found for "{subAmenitySearch}".
                                                </div>
                                            ) : (
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "12px" }}>
                                                    {allSubs.map((sub) => {
                                                        const isSelected = projectPayload.amenities.includes(sub._id);
                                                        return (
                                                            <div
                                                                key={sub._id}
                                                                onClick={() => {
                                                                    setProjectPayload((prev) => {
                                                                        const already = prev.amenities.includes(sub._id);
                                                                        return {
                                                                            ...prev,
                                                                            amenities: already
                                                                                ? prev.amenities.filter(id => id !== sub._id)
                                                                                : [...prev.amenities, sub._id]
                                                                        };
                                                                    });
                                                                }}
                                                                style={{
                                                                    display: "flex", flexDirection: "column",
                                                                    alignItems: "center", justifyContent: "center",
                                                                    gap: "8px", padding: "16px 10px", borderRadius: "12px",
                                                                    border: isSelected ? "2px solid #070C18" : "1.5px solid #e5e7eb",
                                                                    background: isSelected ? "#f0f0f8" : "#fafafa",
                                                                    cursor: "pointer", transition: "all 0.18s ease",
                                                                    position: "relative", userSelect: "none"
                                                                }}
                                                            >
                                                                <div style={{
                                                                    position: "absolute", top: 8, right: 8,
                                                                    width: 18, height: 18, borderRadius: 5,
                                                                    border: isSelected ? "2px solid #070C18" : "2px solid #d1d5db",
                                                                    background: isSelected ? "#070C18" : "white",
                                                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                                                }}>
                                                                    {isSelected && (
                                                                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                                                            <polyline points="1.5 6 4.5 9 10.5 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                                {sub.image ? (
                                                                    <img src={sub.image} alt={sub.name} style={{ width: 32, height: 32, objectFit: "contain" }} />
                                                                ) : (
                                                                    <div style={{
                                                                        width: 36, height: 36, borderRadius: "50%",
                                                                        background: isSelected ? "#ede9fe" : "#f3f4f6",
                                                                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16
                                                                    }}>✦</div>
                                                                )}
                                                                <span style={{ fontSize: 11, fontWeight: 500, color: isSelected ? "#070C18" : "#374151", textAlign: "center", lineHeight: 1.3 }}>
                                                                    {sub.name}
                                                                </span>
                                                                <span style={{ fontSize: 10, color: "#9ca3af", textAlign: "center", lineHeight: 1.2 }}>
                                                                    {sub.parentName}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <>
                                        {/* Parent amenity cards — shown when not searching */}
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px", marginTop: "8px" }}>
                                            {projectAmenities.map((amenity) => {
                                                const selectedCount = amenity.sub_amenities?.filter(
                                                    (sub) => projectPayload.amenities.includes(sub._id)
                                                ).length || 0;
                                                const hasSelected = selectedCount > 0;

                                                return (
                                                    <div
                                                        key={amenity._id}
                                                        onClick={() => { setSelectedAmenityParent(amenity); setSubAmenitySearch(""); }}
                                                        style={{
                                                            display: "flex", flexDirection: "column", alignItems: "center",
                                                            justifyContent: "center", gap: "8px", padding: "16px 12px",
                                                            borderRadius: "12px",
                                                            border: hasSelected ? "2px solid #070C18" : "1.5px solid #e5e7eb",
                                                            background: hasSelected ? "#f5f3ff" : "#fafafa",
                                                            cursor: "pointer", transition: "all 0.18s ease",
                                                            position: "relative", userSelect: "none"
                                                        }}
                                                    >
                                                        {hasSelected && (
                                                            <div style={{
                                                                position: "absolute", top: 8, right: 8,
                                                                background: "#070C18", color: "#fff", borderRadius: "50%",
                                                                width: 20, height: 20, fontSize: 11, fontWeight: 600,
                                                                display: "flex", alignItems: "center", justifyContent: "center"
                                                            }}>
                                                                {selectedCount}
                                                            </div>
                                                        )}
                                                        {amenity.image ? (
                                                            <img src={amenity.image} alt={amenity.name} style={{ width: 32, height: 32, objectFit: "contain" }} />
                                                        ) : (
                                                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: hasSelected ? "#ede9fe" : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✦</div>
                                                        )}
                                                        <span style={{ fontSize: 12, fontWeight: 500, color: hasSelected ? "#070C18" : "#374151", textAlign: "center", lineHeight: 1.3 }}>
                                                            {amenity.name}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}

                                {projectPayload.amenities.length > 0 && (
                                    <div style={{ marginTop: 14, fontSize: 13, color: "#070C18", fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                                        {projectPayload.amenities.length} sub-amenit{projectPayload.amenities.length === 1 ? "y" : "ies"} selected
                                    </div>
                                )}
                            </>
                        )}

                        {/* Sub-amenities Modal */}
                        {selectedAmenityParent && (
                            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <div style={{ background: "#fff", borderRadius: 20, padding: 28, width: "90%", maxWidth: 680, maxHeight: "85vh", display: "flex", flexDirection: "column", gap: 16 }}>
                                    {/* Modal Header */}
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            {selectedAmenityParent.image && (
                                                <img src={selectedAmenityParent.image} alt={selectedAmenityParent.name} style={{ width: 36, height: 36, objectFit: "contain" }} />
                                            )}
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{selectedAmenityParent.name}</h3>
                                                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
                                                    {selectedAmenityParent.sub_amenities?.length || 0} options available
                                                </p>
                                            </div>
                                        </div>
                                        <button type="button" className="erp-btn erp-btn--cancel" onClick={() => setSelectedAmenityParent(null)}>Done</button>
                                    </div>

                                    {/* Sub-amenities grid */}
                                    <div style={{ overflowY: "auto", flex: 1 }}>
                                        {!selectedAmenityParent.sub_amenities || selectedAmenityParent.sub_amenities.length === 0 ? (
                                            <div style={{ textAlign: "center", color: "#9ca3af", padding: "30px 0", fontSize: 14 }}>
                                                No sub-amenities available for this category.
                                            </div>
                                        ) : (
                                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "12px" }}>
                                                {selectedAmenityParent.sub_amenities
                                                    .filter((sub) => !sub.is_deleted)
                                                    .map((sub) => {
                                                        const isSelected = projectPayload.amenities.includes(sub._id);
                                                        return (
                                                            <div
                                                                key={sub._id}
                                                                onClick={() => {
                                                                    setProjectPayload((prev) => {
                                                                        const already = prev.amenities.includes(sub._id);
                                                                        return { ...prev, amenities: already ? prev.amenities.filter((id) => id !== sub._id) : [...prev.amenities, sub._id] };
                                                                    });
                                                                }}
                                                                style={{
                                                                    display: "flex", flexDirection: "column", alignItems: "center",
                                                                    justifyContent: "center", gap: "8px", padding: "16px 10px",
                                                                    borderRadius: "12px",
                                                                    border: isSelected ? "2px solid #070C18" : "1.5px solid #e5e7eb",
                                                                    background: isSelected ? "#f0f0f8" : "#fafafa",
                                                                    cursor: "pointer", transition: "all 0.18s ease",
                                                                    position: "relative", userSelect: "none"
                                                                }}
                                                            >
                                                                <div style={{ position: "absolute", top: 8, right: 8, width: 18, height: 18, borderRadius: 5, border: isSelected ? "2px solid #070C18" : "2px solid #d1d5db", background: isSelected ? "#070C18" : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                    {isSelected && (
                                                                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                                                            <polyline points="1.5 6 4.5 9 10.5 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                                {sub.image ? (
                                                                    <img src={sub.image} alt={sub.name} style={{ width: 32, height: 32, objectFit: "contain" }} />
                                                                ) : (
                                                                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: isSelected ? "#ede9fe" : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>✦</div>
                                                                )}
                                                                <span style={{ fontSize: 11, fontWeight: 500, color: isSelected ? "#070C18" : "#374151", textAlign: "center", lineHeight: 1.3 }}>
                                                                    {sub.name}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                    </div>

                                    {/* Modal footer */}
                                    {projectPayload.amenities.filter((id) =>
                                        selectedAmenityParent.sub_amenities?.some((s) => s._id === id)
                                    ).length > 0 && (
                                            <div style={{ paddingTop: 14, borderTop: "1px solid #e4e7ec", fontSize: 13, color: "#070C18", fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                                                {projectPayload.amenities.filter((id) => selectedAmenityParent.sub_amenities?.some((s) => s._id === id)).length} selected in this category
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── 08 BRANDS NEARBY ────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">08</span>
                            <h2 className="erp-card-title">Brands Nearby</h2>
                        </div>
                        <div className="erp-field">
                            <label className="erp-label">Select Brands</label>
                            <BrandsNearbySelector
                                selected={projectPayload.brands_nearby}
                                onChange={(brands) => setProjectPayload((prev) => ({ ...prev, brands_nearby: brands }))}
                            />
                        </div>
                        <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px dashed #e4e7ec" }}>
                            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12, fontWeight: 500 }}>Can't find a brand? Add it manually:</p>
                            <ManualBrandEntry onAdd={(brand) => setProjectPayload((prev) => ({ ...prev, brands_nearby: [...prev.brands_nearby, brand] }))} />
                            {projectPayload.brands_nearby.filter((b) => b._manual).length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
                                    {projectPayload.brands_nearby.filter((b) => b._manual).map((b, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", background: "#f5f3ff", border: "1.5px solid #a78bfa", borderRadius: 10, fontSize: 13 }}>
                                            {b.image && <img src={b.image} alt={b.name} style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover" }} />}
                                            <span style={{ fontWeight: 500, color: "#111827" }}>{b.name}</span>
                                            <button type="button" onClick={() => setProjectPayload((prev) => ({ ...prev, brands_nearby: prev.brands_nearby.filter((item) => !(item._manual && item.name === b.name)) }))}
                                                style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── 09 PROJECT HIGHLIGHTS ───────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">09</span>
                            <h2 className="erp-card-title">Project Highlights</h2>
                        </div>
                        {projectHighlight.map((highlight, index) => (
                            <div key={index} style={{ background: "#f9fafb", border: "1px solid #e4e7ec", borderRadius: 12, padding: 18, marginBottom: 14 }}>
                                <div className="erp-grid erp-grid-2" style={{ marginBottom: 10 }}>
                                    <div className="erp-field">
                                        <label className="erp-label">Title</label>
                                        <input className="erp-input" type="text" placeholder="Highlight title" value={highlight.title} onChange={(e) => handleHighlightChange(index, "title", e.target.value)} />
                                    </div>
                                    <div className="erp-field">
                                        <label className="erp-label">Image</label>
                                        {highlight.image && (
                                            <div style={{ position: "relative", display: "inline-block", marginBottom: 4 }}>
                                                <img src={highlight.image} alt="hl" style={{ height: 40, borderRadius: 6, objectFit: "contain", display: "block" }} />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveHighlightImage(index)}
                                                    style={{ position: "absolute", top: 2, left: 60, background: "#ef4444", border: "none", borderRadius: "50%", width: 18, height: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                        <path d="M10 11v6M14 11v6" />
                                                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                        <input className="erp-input" type="file" accept="image/*" onChange={(e) => handleHighlightImageUpload(e, index)} />
                                    </div>
                                </div>
                                <div className="erp-field" style={{ marginBottom: 8 }}>
                                    <label className="erp-label">Description</label>
                                    <textarea className="erp-textarea" rows={2} value={highlight.description} onChange={(e) => handleHighlightChange(index, "description", e.target.value)} />
                                </div>
                                {projectHighlight.length > 1 && (
                                    <button type="button" className="erp-furnish-btn erp-furnish-btn--clear" onClick={() => removeHighlight(index)}>Remove</button>
                                )}
                            </div>
                        ))}
                        <button type="button" className="erp-furnish-btn" onClick={addHighlight}>+ Add Highlight</button>
                    </div>

                    {/* ── 10 ADDITIONAL INFO ──────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">10</span>
                            <h2 className="erp-card-title">Additional Info</h2>
                        </div>
                        <div className="erp-grid erp-grid-2">
                            <div className="erp-field">
                                <label className="erp-label">About Project <span className="erp-req">*</span></label>
                                <ReactQuill
                                    theme="snow"
                                    value={projectPayload.about_project}
                                    onChange={(value) => setProjectPayload((prev) => ({ ...prev, about_project: value }))}
                                    placeholder="Enter project details..."
                                    style={{ background: "#fff", borderRadius: 8 }}
                                />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">About Builder</label>
                                <textarea className="erp-textarea" name="about_builder" value={projectPayload.about_builder} onChange={handleAddChange} rows={3} placeholder="Enter builder details..." />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Note (Optional)</label>
                                <textarea className="erp-textarea" name="note" value={projectPayload.note} onChange={handleAddChange} rows={3} placeholder="Any special note..." />
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">Payment Note (Optional)</label>
                                <textarea className="erp-textarea" name="payment_note" value={projectPayload.payment_note} onChange={handleAddChange} rows={3} placeholder="Any payment note..." />
                            </div>
                        </div>
                    </div>

                    {/* ── 11 BROCHURE ─────────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">11</span>
                            <h2 className="erp-card-title">Brochure</h2>
                        </div>
                        <div className="erp-field">
                            <label className="erp-label">Upload Brochure (PDF)</label>
                            <input className="erp-input" type="file" accept=".pdf" ref={brochureRef} disabled={!!projectPayload.brochure || brochureUploading} onChange={handleBrochureUpload} />
                            {brochureUploading && <p style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>Uploading...</p>}
                            {projectPayload.brochure && (
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                                    <a href={projectPayload.brochure} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#10b981", fontWeight: 500 }}>✓ View Brochure</a>
                                    <button type="button" className="erp-furnish-btn erp-furnish-btn--clear" style={{ padding: "4px 10px", fontSize: 12 }}
                                        onClick={() => { setProjectPayload((prev) => ({ ...prev, brochure: "" })); if (brochureRef.current) brochureRef.current.value = ""; }}>
                                        Replace
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── 12 FAQs ─────────────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">12</span>
                            <h2 className="erp-card-title">FAQs <span className="erp-req">*</span></h2>
                        </div>
                        {faqs.map((faq, index) => (
                            <div key={index} style={{ background: "#f9fafb", border: "1px solid #e4e7ec", borderRadius: 12, padding: 18, marginBottom: 14 }}>
                                <div className="erp-grid erp-grid-2" style={{ marginBottom: 10 }}>
                                    <div className="erp-field">
                                        <label className="erp-label">Question</label>
                                        <input className="erp-input" type="text" placeholder="Enter question" value={faq.question} onChange={(e) => handleFaqChange(index, "question", e.target.value)} />
                                    </div>
                                    <div className="erp-field">
                                        <label className="erp-label">Answer</label>
                                        <input className="erp-input" type="text" placeholder="Enter answer" value={faq.answer} onChange={(e) => handleFaqChange(index, "answer", e.target.value)} />
                                    </div>
                                </div>
                                {faqs.length > 1 && (
                                    <button type="button" className="erp-furnish-btn erp-furnish-btn--clear" onClick={() => removeFaq(index)}>Remove</button>
                                )}
                            </div>
                        ))}
                        <button type="button" className="erp-furnish-btn" onClick={addFaq}>+ Add FAQ</button>
                    </div>

                    {/* ── 13 NEWS ─────────────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">13</span>
                            <h2 className="erp-card-title">News (Optional)</h2>
                        </div>
                        {news.map((item, index) => (
                            <div key={index} style={{ background: "#f9fafb", border: "1px solid #e4e7ec", borderRadius: 12, padding: 18, marginBottom: 14 }}>
                                <div className="erp-grid erp-grid-2" style={{ marginBottom: 10 }}>
                                    <div className="erp-field">
                                        <label className="erp-label">Title</label>
                                        <input className="erp-input" type="text" placeholder="News title" value={item.title} onChange={(e) => handleNewsChange(index, "title", e.target.value)} />
                                    </div>
                                    <div className="erp-field">
                                        <label className="erp-label">Image</label>
                                        {item.image && (
                                            <div style={{ position: "relative", display: "inline-block", marginBottom: 4 }}>
                                                <img src={item.image} alt="news" style={{ height: 40, borderRadius: 6, objectFit: "contain", display: "block" }} />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveNewsImage(index)}
                                                    style={{ position: "absolute", top: 1, left: 40, background: "#ef4444", border: "none", borderRadius: "50%", width: 18, height: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                        <path d="M10 11v6M14 11v6" />
                                                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                        <input className="erp-input" type="file" accept="image/*" onChange={(e) => handleNewsImageUpload(e, index)} />
                                    </div>
                                </div>
                                <div className="erp-field" style={{ marginBottom: 8 }}>
                                    <label className="erp-label">Description</label>
                                    <textarea className="erp-textarea" rows={2} value={item.description} onChange={(e) => handleNewsChange(index, "description", e.target.value)} />
                                </div>
                                <div className="erp-field" style={{ marginBottom: 8 }}>
                                    <label className="erp-label">Link</label>
                                    <input className="erp-input" type="url" placeholder="https://example.com/news-article" value={item.link || ""} onChange={(e) => handleNewsChange(index, "link", e.target.value)} />
                                </div>
                                {news.length > 1 && (
                                    <button type="button" className="erp-furnish-btn erp-furnish-btn--clear" onClick={() => removeNews(index)}>Remove</button>
                                )}
                            </div>
                        ))}
                        <button type="button" className="erp-furnish-btn" onClick={addNews}>+ Add News</button>
                    </div>

                    {/* ── 14 YOUTUBE LINKS ────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">14</span>
                            <h2 className="erp-card-title">YouTube Links (Optional)</h2>
                        </div>
                        {ytLinks.map((yt, index) => (
                            <div key={index} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                                <div className="erp-field" style={{ flex: 1 }}>
                                    <label className="erp-label">YouTube URL</label>
                                    <input className="erp-input" type="url" placeholder="https://youtube.com/watch?v=..." value={yt.url} onChange={(e) => handleYtChange(index, e.target.value)} />
                                </div>
                                {ytLinks.length > 1 && (
                                    <button type="button" className="erp-tag-remove" style={{ marginTop: 24, fontSize: 20 }} onClick={() => removeYtLink(index)}>×</button>
                                )}
                            </div>
                        ))}
                        <button type="button" className="erp-furnish-btn" onClick={addYtLink}>+ Add YouTube Link</button>
                    </div>

                    {/* ── 15 LANDMARKS ────────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">15</span>
                            <h2 className="erp-card-title">Landmarks (Optional)</h2>
                        </div>
                        <div className="erp-field">
                            <label className="erp-label">Add Landmark</label>
                            <div style={{ display: "flex", gap: 8 }}>
                                <input
                                    className="erp-input"
                                    type="text"
                                    placeholder="Enter landmark name and press Add"
                                    value={landmarkInput}
                                    onChange={(e) => setLandmarkInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            if (!landmarkInput.trim()) return;
                                            setProjectPayload((prev) => ({ ...prev, landmarks: [...prev.landmarks, landmarkInput.trim()] }));
                                            setLandmarkInput("");
                                        }
                                    }}
                                />
                                <button type="button" className="erp-furnish-btn"
                                    onClick={() => {
                                        if (!landmarkInput.trim()) return;
                                        setProjectPayload((prev) => ({ ...prev, landmarks: [...prev.landmarks, landmarkInput.trim()] }));
                                        setLandmarkInput("");
                                    }}>
                                    Add
                                </button>
                            </div>
                            {projectPayload.landmarks.length > 0 && (
                                <div className="erp-tags-row" style={{ marginTop: 10 }}>
                                    {projectPayload.landmarks.map((item, i) => (
                                        <span key={i} className="erp-tag">
                                            {item}
                                            <button type="button" className="erp-tag-remove"
                                                onClick={() => setProjectPayload((prev) => ({ ...prev, landmarks: prev.landmarks.filter((_, idx) => idx !== i) }))}>×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── 16 IMAGES ───────────────────────────────────────────────────────── */}
                    <div className="erp-card">
                        <div className="erp-card-header">
                            <span className="erp-card-badge">16</span>
                            <h2 className="erp-card-title">Images</h2>
                        </div>
                        <div className="erp-grid erp-grid-2">
                            <div className="erp-field">
                                <label className="erp-label">Cover Photo <span style={{ color: "red" }}> *</span></label>
                                <input className="erp-input" type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handleCoverChange} />
                                {coverImgPreview && (
                                    <div style={{ position: "relative", marginTop: 10, display: "inline-block" }}>
                                        <img src={coverImgPreview} alt="cover" style={{ height: 100, borderRadius: 10, objectFit: "cover", display: "block" }} />
                                        <button type="button" onClick={handleRemoveCoverImage} style={{ position: "absolute", top: 4, left: 10, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6M14 11v6" />
                                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="erp-field">
                                <label className="erp-label">
                                    Project Images <span style={{ color: "red" }}> *</span>
                                    <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>{existingProjectImgs.length + newProjectImgs.length} / 25</span>
                                </label>
                                <input className="erp-input" type="file" accept=".jpg,.jpeg,.png,.webp" multiple onChange={handleProjectImageChange} />
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                                    {existingProjectImgs.map((img, i) => (
                                        <div key={`existing-${i}`} style={{ position: "relative" }}>
                                            <img src={img.url} alt={`existing-${i}`} style={{ width: 72, height: 72, borderRadius: 8, objectFit: "cover" }} />
                                            <button type="button" onClick={() => handleRemoveExistingImage(i)} style={{ position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                    <path d="M10 11v6M14 11v6" />
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                    {newProjectImgPreviews.map((src, i) => (
                                        <div key={`new-${i}`} style={{ position: "relative" }}>
                                            <img src={src} alt={`new-${i}`} style={{ width: 72, height: 72, borderRadius: 8, objectFit: "cover", border: "2px solid #8b5cf6" }} />
                                            <button type="button" onClick={() => handleRemoveNewImage(i)} style={{ position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                    <path d="M10 11v6M14 11v6" />
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {newProjectImgs.length > 0 && (
                                    <p style={{ fontSize: 12, color: "#8b5cf6", marginTop: 6 }}>Purple border = newly added images</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
                    <div className="erp-footer">
                        <Link to="/resale_project" className="erp-btn erp-btn--cancel">Cancel</Link>
                        <button type="submit" className="erp-btn erp-btn--submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: "erp-spin 0.7s linear infinite" }}>
                                        <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                                        <path d="M12 2a10 10 0 0 1 10 10" />
                                    </svg>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    Update Project
                                </>
                            )}
                        </button>
                    </div>

                </form>
            </div>

            {/* ── Investment Features Modal (same as Add) ──────────────────────────── */}
            {openInvestmentModal && (
                <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: "90%", maxWidth: 480 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Investment Features</h3>
                            <button type="button" className="erp-btn erp-btn--cancel" onClick={() => setOpenInvestmentModal(false)}>Done</button>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {fields.map((field) => {
                                const key = fieldKeyMap[field];
                                const isOn = !!projectPayload[key];
                                return (
                                    <div key={field} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e4e7ec", background: isOn ? "#f5f3ff" : "#fafafa" }}>
                                        <span style={{ fontSize: 14, fontWeight: 500, color: isOn ? "#070C18" : "#374151" }}>{field}</span>
                                        <div
                                            onClick={() => setProjectPayload((prev) => ({ ...prev, [key]: !prev[key] }))}
                                            style={{ width: 42, height: 24, borderRadius: 12, background: isOn ? "#070C18" : "#d1d5db", cursor: "pointer", position: "relative", transition: "background 0.2s" }}
                                        >
                                            <div style={{ position: "absolute", top: 3, left: isOn ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
};

export default EditProject;